/**
 * Amorçage d'une visite par lien public.
 *
 * Le lien ne crée pas de compte : il ouvre un périmètre de lecture en base.
 * Ce hook résout l'entreprise correspondante — les policies `*_public_link_view`
 * font que `companies` ne contient qu'une seule ligne visible — puis alimente le
 * store avec un profil synthétique de rôle client. À partir de là, tous les
 * écrans client existants fonctionnent tels quels.
 */

import { useQuery } from '@tanstack/react-query';
import { publicLink } from '@/lib/supabase';
import { CompanyRepository } from '@/repositories/CompanyRepository';
import { useAppStore } from '@/store/useAppStore';
import { Role, UserStatus } from '@/types';

/** Identifiant du profil synthétique — ne correspond à aucune ligne de `profiles` */
export const PUBLIC_LINK_PROFILE_ID = 'public-link';

export interface PublicLinkCompany {
  id: string;
  name: string;
  logo_url: string | null;
}

export function usePublicLinkSession() {
  return useQuery<PublicLinkCompany>({
    queryKey: ['public-link-session', publicLink?.slug, publicLink?.token],
    queryFn: async () => {
      const data = await CompanyRepository.getByPublicLink();
      if (!data) throw new Error('public_link_not_found');

      // Hydratation impérative du store, hors rendu : le profil synthétique est
      // ce que lisent DashboardClient, Projects, ProjectDetail, etc.
      useAppStore.setState({
        profile: {
          id: PUBLIC_LINK_PROFILE_ID,
          role: Role.CLIENT,
          first_name: data.name,
          company_id: data.id,
          status: UserStatus.ACTIVE,
          permissions: [],
        },
        loading: false,
      });

      return data;
    },
    enabled: !!publicLink,
    // Un lien révoqué ne doit pas être réessayé en boucle
    retry: false,
    staleTime: Infinity,
  });
}
